import type { Link, PhrasingContent } from 'mdast';
import type { Path, NodeEntry, Text } from 'slate';

import { Node } from 'slate';

import { safelyParseJSON, safelyStringifyJSON } from '@core/utils/json';

import { findFirst, findLast } from '@ui/MarkdownEditor/editor/utils';
import type {
  Deserializer,
  FormattedText,
  ImageBlock,
  ImageElement,
  LinkElement,
  MarkdownCharInline,
  PlainMdNode,
  Serializer,
} from '@ui/MarkdownEditor/types';

import { isImage, toString as imageToString } from '../Image/shared';
import { isImageBlock, toString as imageBlockToString } from '../ImageBlock/shared';

import { type, toString, defaultLink } from './shared';

const set = (node: LinkElement, path: Path, element: { text: string }) => {
  const [lastPath, ...paths] = path.reverse();
  let ref: FormattedText | LinkElement | MarkdownCharInline = node;

  paths.forEach(p => {
    if ('children' in ref) ref = ref.children[p];
  });

  ref.children[lastPath] = element;
};

/* @note: this is to workaround https://linear.app/readme-io/issue/RM-4501/link-within-image-not-rendered-in-new-editor
 * An alternative solution might be to allow images as inline and improve the
 * image menu, but that will require considerable more design and architecture
 * work. :shrug:
 */
const imagesToPlainText = (link: LinkElement): LinkElement => {
  const updates: NodeEntry<ImageBlock | ImageElement>[] = [];

  for (const [image, path] of Node.descendants(link)) {
    if (isImage(image) || isImageBlock(image)) {
      updates.push([image, path]);
    }
  }

  updates.forEach(([image, path]) => {
    const imgString = isImageBlock(image) ? imageBlockToString(image) : imageToString(image);
    set(link, path, { text: imgString });
  });

  return link;
};

/*
 * This is deeply tied to the editor's link implementation. At the time of
 * this writing, a link is represented roughly as:
 * {
 *   type: 'link',
 *   url: 'https://example.com',
 *   title: 'Example', // optional
 *   children: [
 *     { text: '[label](' },
 *     {
 *       type: 'markdown-char',
 *       char: '🔗', children: [{ text: '' }]
 *     },
 *     { text: ')' }
 *   ]
 * }
 *
 * However, the label portion can be contain arbitrary formatted text. So one
 * way to serialize is the extract the label tree, and reconstruct the link
 * markdown node manually, as opposed the previous approach of serializing it
 * to string and reparsing it to a markdown node.
 */
const extractLabel = (link: LinkElement): Text[] => {
  const labelNodes = link.url ? link.children.slice(0, -2) : link.children;

  // @note: slate uses immer, and for some reason these objects can be frozen
  const label = safelyParseJSON(safelyStringifyJSON(labelNodes) || '');
  if (!label || label.length === 0) return [];

  findFirst({ children: label }, first => {
    first.text = first.text.replace(/^\[/, '');
  });
  findLast({ children: label }, last => {
    last.text = last.text.replace(link.url ? /\]\($/ : /\]\(\)$/, '');
  });

  return label;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const deserialize: Deserializer<Link, LinkElement> = ({ position, ...node }, deeper) => {
  const children = deeper(node) as Text[];

  const link: LinkElement = defaultLink({
    ...node,
    title: node.title ?? undefined,
    type,
    children,
  });

  imagesToPlainText(link);
  link.label = link.children
    .slice(1, link.children.length - 3)
    .map(n => Node.string(n))
    .join('');

  return link;
};

export const serialize: Serializer<LinkElement, Link | PlainMdNode, PhrasingContent[]> = (
  node,
  deeper,
  { renderingLibrary },
) => {
  if ('mdx' in renderingLibrary) {
    const labelChildren = extractLabel(node);
    const children = deeper({ children: labelChildren } as LinkElement);

    return {
      ...node,
      children,
    } satisfies Link;
  }

  const tree = renderingLibrary.mdast(toString(node, { title: true }));
  const astNode = tree.children[0].children[0] as Link;
  const children = astNode.children;

  return {
    /*
     * The above parsing to ast should be refactored, but for now it works.
     * However, it doesn't support adding extended properties like `title`.
     */
    ...node,
    children,
  };
};
